import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Stack, Typography } from '@mui/material';
import HelpPopover from '../../../HelpPopover';
import { updateParameter } from '../DeviceCardBuilder';
import { DeviceCardProps } from '../DeviceCardProps';
import { useControlInitialization } from '../../../../hooks/controls/useControlInitialization';
import UnsupportedCodeStatus from './UnsupportedCodeStatus';
import { isUnsupportedEnumRawValue, parseKnownEnumRawValue } from './unsupportedEnumValue';
import { useTranslation } from 'react-i18next';

const UNSUPPORTED_OPTION_VALUE = '__unsupported_custom_code__';

interface DropDownControlProps extends DeviceCardProps {
  labelId: string;
  label: React.ReactNode;
  selectId: string;
  selectLabelId: string;
  labelPopover: React.ReactNode;
  labelPopoverId: string;
  options: { label: string; value: string }[];
}

const DropDownControl = (props: DropDownControlProps) => {
  const { t } = useTranslation();
  const optionValues = props.options.map((option) => option.value);
  const fallbackValue = props.options[0]?.value ?? '';
  const {
    value: selectedValue,
    setValue: setSelectedValue,
    unsupportedRawValue,
  } = useControlInitialization<string>(props, {
    parseValue: (v) => parseKnownEnumRawValue(v, optionValues, fallbackValue),
    isUnsupportedRawValue: (rawValue) => isUnsupportedEnumRawValue(rawValue, optionValues),
  });

  const onChange = (value: string) => {
    if (value === UNSUPPORTED_OPTION_VALUE) return;
    setSelectedValue(value);
    updateParameter(props, value);
  };

  // Ensure value is always defined - use empty string if undefined, or validate against options
  const isUnsupported = Boolean(unsupportedRawValue);
  const safeValue = isUnsupported
    ? UNSUPPORTED_OPTION_VALUE
    : selectedValue !== undefined && props.options.some((opt) => opt.value === selectedValue)
    ? selectedValue
    : '';

  return (
    <Stack direction="column" spacing={2}>
      <InputLabel id={props.labelId}>
        <Stack direction="row" spacing={1}>
          <Typography>{props.label}</Typography>
          <HelpPopover id={props.labelPopoverId} text={props.labelPopover} />
        </Stack>
      </InputLabel>
      <FormControl variant="standard" sx={{ width: 100, marginTop: '0px !important' }}>
        <Select labelId={props.labelId} id={props.selectId} value={safeValue} onChange={(event: SelectChangeEvent) => onChange(event.target.value)}>
          {isUnsupported && (
            <MenuItem value={UNSUPPORTED_OPTION_VALUE} disabled>
              {t('controlPanel.devices.unsupportedCode.title', { defaultValue: 'Custom code' })}
            </MenuItem>
          )}
          {props.options.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <UnsupportedCodeStatus
        code={unsupportedRawValue}
        descriptionI18nKey="controlPanel.devices.unsupportedEnumParameter.description"
        descriptionDefaultValue="Unsupported setting value: {{code}}"
      />
    </Stack>
  );
};

export default DropDownControl;
